/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useState } from 'react';
import { DataTable } from '@scuf/datatable';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import './EmaasEnergyUsagesBreakdownByMeterTheme.scss';
import {
  EnergyUsagesDetailExpandMeterField,
  KPI_VALUE_DECIMAL_PRECISION
} from '../../../shared/Utils/Constants';
import {
  getConvertedUnit,
  getMaxValueIndexForField,
  getNullConverted
} from '../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import EnergyActualConsumptionComponent from '../../DataTableComponents/EnergyUsages/CellDataRenderer/EnergyActualConsumptionComponent';
import EnergyBreakDownChangeComponent from '../../DataTableComponents/EnergyUsages/CellDataRenderer/EnergyBreakDownChangeComponent';
import EnergyBreakDownMeterComponent from '../../DataTableComponents/EnergyUsages/CellDataRenderer/EnergyBreakDownMeterComponent';
import EnergyBreakDownBaselineComponent from '../../DataTableComponents/EnergyUsages/CellDataRenderer/EnergyBreakDownBaselineComponent';
import { IBreakdownByFloor } from './IEnergyUsagesBreakdownProps';
import { IAny } from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import {
  expanderTemplate,
  renderTableColumns
} from '../../../shared/MeterSavingsDetailTable/MeterSavingsDetailTableUtility';
import { rowExpansionTemplate } from '../../PortfolioEnergyUsageViewDetails/PremiumSubscription/AllUtilitiesDetails/BottomSection/TableColumnRenderUtils';
import { Icon } from '@scuf/common';
import { ApiContext } from '../../../../context/ApiContext';
import { ConversionType, getConvertedValue } from '../../../../main';

const emaasDetailBreakdownByFloor = (props: IAny) => {
  const { meterData, columnDetails, energyMeasurementUnit, sourceType } = props;
  const {
    onClickViewDetailsMeterTrend,
    enableViewDetailsMeterTrend,
    userPreferedUnit
  } = useContext(ApiContext);
  const [expandedRows, setexpandedRows] = useState([
    meterData[getMaxValueIndexForField(meterData, 'totalActualEmission')]
  ]);
  const [fieldSortOrder, setFieldSortOrder] = useState<number>(-1);
  const [sortFieldName, setSortFieldName] = useState<string>(
    EnergyUsagesDetailExpandMeterField.actualConsumption
  );
  const cellDataRenderer = (cellData: ICellData) => {
    const { value, field, rowData } = cellData;
    if (field === EnergyUsagesDetailExpandMeterField.actualConsumption) {
      const { totalActualEmission: convertedValue } = rowData;
      return (
        <EnergyActualConsumptionComponent
          value={value}
          unit={rowData.actualUnit}
          savings={
            rowData.rawTotalDeviation === null ? 0 : rowData.rawTotalDeviation
          }
          savingsPercentage={
            rowData.rawTotalDeviation === null
              ? 0
              : rowData.percentageEmissionsSaving
          }
          absoluteActual={rowData.rawTotalActualEmission}
          absolutePrevPeriod={
            rowData.rawTotalAbsoluteBaselineConsumption === 0
              ? null
              : rowData.rawTotalAbsoluteBaselineConsumption
          }
          progessBarRelativeWidth={rowData.progessBarRelativeWidth}
          energyMeasurementUnit={energyMeasurementUnit}
          sourceType={
            sourceType.toLowerCase() === 'all'
              ? ConversionType.ELECTRICITY
              : sourceType
          }
        />
      );
    }
    if (field === EnergyUsagesDetailExpandMeterField.change) {
      const { totalDeviation: convertedValue, deviationScaledData } = rowData;
      let changeProps;
      changeProps = {
        value: rowData.totalDeviation === null ? null : rowData.totalDeviation,
        unit: rowData.changeUnit,
        energyMeasurementUnit: energyMeasurementUnit,
        percentageEmissionsSaving: rowData.percentageEmissionsSaving,
        sourceType:
          sourceType.toLowerCase() === 'all'
            ? ConversionType.ELECTRICITY
            : sourceType
      };
      // if (
      //   convertedValue === 0 &&
      //   deviationScaledData &&
      //   deviationScaledData.Value !== 0
      // ) {
      //   changeProps = {
      //     ...changeProps,
      //     value: deviationScaledData.Value,
      //     unit: deviationScaledData.Unit,
      //     energyMeasurementUnit
      //   };
      // }
      return <EnergyBreakDownChangeComponent {...changeProps} />;
    }
    if (field === EnergyUsagesDetailExpandMeterField.meter) {
      const meterProps = {
        totalDeviation: rowData.percentageEmissionsSaving,
        utilityType: rowData.utilityType,
        isIconShow: false,
        meterCount: rowData?.utilityMeters?.length
      };
      return <EnergyBreakDownMeterComponent {...meterProps} />;
    }
    if (field === EnergyUsagesDetailExpandMeterField.baselineConsumption) {
      const { totalPreviousPeriodEmission: convertedValue } = rowData;
      const actualBaseLine =
        convertedValue === null
          ? null
          : Number(convertedValue).toFixed(KPI_VALUE_DECIMAL_PRECISION);
      const baselineProps = {
        value: value,
        unit: rowData.baselineUnit,
        energyMeasurementUnit: energyMeasurementUnit,
        sourceType:
          sourceType.toLowerCase() === 'all'
            ? ConversionType.ELECTRICITY
            : sourceType
      };
      return <EnergyBreakDownBaselineComponent {...baselineProps} />;
    }
    return <></>;
  };

  const rowcellDataRenderer = (cellData: ICellData) => {
    const { value, field, rowData } = cellData;
    if (field === EnergyUsagesDetailExpandMeterField.actualConsumption) {
      const { totalActualEmission: convertedValue } = rowData;
      return (
        <EnergyActualConsumptionComponent
          value={value}
          unit={rowData.actualUnit}
          savings={
            rowData.rawTotalDeviation === null ? 0 : rowData.rawTotalDeviation
          }
          savingsPercentage={
            rowData.rawTotalDeviation === null
              ? 0
              : rowData.percentageEmissionsSaving
          }
          progessBarRelativeWidth={rowData.progessBarRelativeWidth}
          absoluteActual={rowData.rawTotalActualEmission}
          absolutePrevPeriod={
            rowData.rawTotalAbsoluteBaselineConsumption === 0
              ? null
              : rowData.rawTotalAbsoluteBaselineConsumption
          }
          energyMeasurementUnit={energyMeasurementUnit}
          sourceType={
            sourceType.toLowerCase() === 'all'
              ? ConversionType.ELECTRICITY
              : sourceType
          }
        />
      );
    }

    if (field === EnergyUsagesDetailExpandMeterField.meter) {
      const meterProps = {
        totalDeviation: rowData.totalDeviationPercentage,
        utilityType: rowData.meterName,
        isIconShow: true
      };
      return <EnergyBreakDownMeterComponent {...meterProps} />;
    }

    if (field === EnergyUsagesDetailExpandMeterField.baselineConsumption) {
      const { totalPreviousPeriodEmission: convertedValue } = rowData;
      return value === null ? (
        <>--</>
      ) : (
        <span className="score-category-meterdata-meter floor-wise-baseline-consumption">
          {
            getConvertedValue(
              sourceType
                ? sourceType.toLowerCase() === 'all'
                  ? ConversionType?.ELECTRICITY
                  : sourceType
                : ConversionType?.ELECTRICITY,
              energyMeasurementUnit,
              Number(value),
              rowData.baselineUnit
            ).Value
          }{' '}
          {/* {getNullConverted(convertedValue)}{' '} */}
          {getConvertedUnit(value, rowData.baselineUnit)}
        </span>
      );
    }
    if (field === EnergyUsagesDetailExpandMeterField.change) {
      const { totalDeviation: convertedValue, deviationScaledData } = rowData;
      let changeProps;
      changeProps = {
        value: convertedValue,
        unit: rowData.changeUnit,
        percentageEmissionsSaving: rowData.totalDeviationPercentage,
        energyMeasurementUnit: energyMeasurementUnit,
        sourceType:
          sourceType.toLowerCase() === 'all'
            ? ConversionType.ELECTRICITY
            : sourceType
      };
      // if (
      //   convertedValue === 0 &&
      //   deviationScaledData &&
      //   deviationScaledData.Value !== 0
      // ) {
      //   changeProps = {
      //     ...changeProps,
      //     value: deviationScaledData.Value,
      //     unit: deviationScaledData.Unit
      //   };
      // }
      return <EnergyBreakDownChangeComponent {...changeProps} />;
    }
    if (field === EnergyUsagesDetailExpandMeterField.eqdetails) {
      return (
        <div
          className="trend-vis-icon-wrapper"
          onClick={(e) => {
            enableViewDetailsMeterTrend &&
              onClickViewDetailsMeterTrend &&
              onClickViewDetailsMeterTrend(rowData.meterId, rowData.meterName);
          }}
        >
          <Icon name="graph" root="global" />
        </div>
      );
    }
    return <></>;
  };

  return (
    <div className="Savings-Datatable">
      <DataTable
        className="nonflat-table Savingtable"
        expandedRows={expandedRows}
        rowExpansionTemplate={(data: IAny) =>
          rowExpansionTemplate(data, columnDetails, rowcellDataRenderer)
        }
        data={meterData}
        onSort={({ sortField, sortOrder }) => {
          setFieldSortOrder(sortOrder as number);
          setSortFieldName(sortField);
        }}
        sortOrder={fieldSortOrder}
        sortField={sortFieldName}
      >
        <DataTable.Column
          initialWidth="1.5%"
          header=""
          field=""
          renderer={(data: ICellData) =>
            expanderTemplate(data, expandedRows, setexpandedRows)
          }
        />
        {renderTableColumns(columnDetails, cellDataRenderer)}
      </DataTable>
    </div>
  );
};
export default emaasDetailBreakdownByFloor;
